import Component from './Component';
import GeojsonTransform from '../geometry/GeojsonTransform';

/**
 * Geojson对象坐标转组件
 */
export default class GeojsonConverter extends Component {
    /**
     * 构造方法
     * @param {object}      可选项
     * @returns {undefined}
     */
    constructor(options) {
        super({
            precious: 7, // 经纬度坐标精度，0:不控制；其他值：四舍五入的小数位数
            ...options
        });
    }

    /**
     * 将经纬度坐标转换为平面像素坐标
     * @param {object<geojson>}   geojson 经纬度坐标
     * @returns {object<geojson>} 平面像素坐标
     */
    lngLatToPixel(geojson) {
        return this.convert(geojson, this._lngLatToPixel);
    }

    /**
     * 将经纬度坐标转换为为屏幕内像素坐标
     * 屏幕内像素坐标为整数，一般用于渲染
     * @param {object<geojson>}   geojson 经纬度坐标
     * @returns {object<geojson>} 屏幕内像素坐标
     */
    lngLatToContainerPoint(geojson) {
        return this.convert(geojson, this._lngLatToContainerPoint);
    }

    /**
     * 将平面像素坐标转换为经纬度坐标
     * @param {object<geojson>}   geojson 平面像素坐标
     * @returns {object<geojson>} 经纬度坐标
     */
    pixelToLngLat(geojson) {
        return this.convert(geojson, this._pixelToLngLat);
    }

    /**
     * 将点经纬度坐标变换为经纬度对象
     * @param {object<geojson>} pointGeo 点经纬度坐标
     * @returns {object<L.LatLng>} 像素点对象
     */
    toLatLng(pointGeo) {
        const lng = pointGeo.coordinates[0];
        const lat = pointGeo.coordinates[1];

        return L.latLng([lat, lng]);
    }

    /**
     * 将点经纬度坐标变换为平面像素点对象
     * @param {object<geojson>} pointGeo 点经纬度坐标
     * @returns {object<L.Point>} 平面像素点对象
     */
    toPoint(pointGeo) {
        const lng = pointGeo.coordinates[0];
        const lat = pointGeo.coordinates[1];
        const zoom = this._map.getZoomLevel();

        return this._map.project([lat, lng], zoom);
    }

    /**
     * 将点经纬度坐标变换为屏幕内像素点对象
     * @param {object<geojson>} pointGeo 点经纬度坐标
     * @returns {object<L.Point>} 屏幕内像素点对象
     */
    toContainerPoint(pointGeo) {
        const lng = pointGeo.coordinates[0];
        const lat = pointGeo.coordinates[1];

        return this._map.latLngToContainerPoint([lat, lng]);
    }

    /**
     * 将平面像素点坐标变换为经纬度坐标geojson对象
     * @param {object<L.Point>} point 平面像素点坐标
     * @returns {object<geojson>} 经纬度坐标
     */
    fromPoint(point) {
        const zoom = this._map.getZoomLevel();

        return this.wrapLatLng(this._map.unproject(point, zoom));
    }

    /**
     * 将经纬度坐标对象包装成geojson格式
     * @param {object<L.LatLng>}  latlng 经纬度坐标对象
     * @returns {object<geojson>} 经纬度坐标geojson
     */
    wrapLatLng(latlng) {
        return {
            type: 'Point',
            coordinates: [latlng.lng, latlng.lat]
        };
    }

    /**
     * 将像素点坐标对象包装成geojson格式
     * @param {object<L.Point>}   point 像素点坐标对象
     * @returns {object<geojson>} 像素点坐标geojson
     */
    wrapPoint(point) {
        return {
            type: 'Point',
            coordinates: [point.x, point.y]
        };
    }

    /**
     * 将像素坐标矩形对象包装成geojson格式
     * @param {object<L.Bounds>}  bounds 像素坐标矩形对象
     * @returns {object<geojson>} 像素坐标geojson
     */
    wrapBounds(bounds) {
        const tl = bounds.getTopLeft();
        const tr = bounds.getTopRight();
        const br = bounds.getBottomRight();
        const bl = bounds.getBottomLeft();

        return {
            type: 'Polygon',
            coordinates: [
                [
                    [tl.x, tl.y],
                    [tr.x, tr.y],
                    [br.x, br.y],
                    [bl.x, bl.y],
                    [tl.x, tl.y],
                ],
            ],
        };
    }

    /**
     * 遍历geojson对象，使用给定的方法转换坐标
     * 不会改变原对象
     * @param {Object}   geojson - 要转换的geoJson对象
     * @param {Function} convertFunc - 点坐标转换函数
     * @returns {Object} newGeojson - 转换后新的geoJson对象
     */
    convert = GeojsonTransform.convert

    /**
     * 将经纬度坐标转换为平面像素坐标
     * @param {array} coordinates - 经纬度坐标
     * @returns {array} - 平面像素坐标
     */
    _lngLatToPixel = coordinates => {
        const x = coordinates[0];
        const y = coordinates[1];
        const zoom = this._map.getZoomLevel();
        const point = this._map.project([y, x], zoom);

        return [point.x, point.y];
    }

    /**
     * 将平面像素坐标转换为经纬度坐标
     * @param {array} coordinates - 平面像素坐标
     * @returns {array} - 经纬度坐标
     */
    _pixelToLngLat = coordinates => {
        const zoom = this._map.getZoomLevel();
        const latlng = this._map.unproject(coordinates, zoom);

        const prec = this.options.precious;
        if (prec) {
            return [parseFloat(latlng.lng.toFixed(prec)), parseFloat(latlng.lat.toFixed(prec))];
        }
        return [latlng.lng, latlng.lat];
    }

    /**
     * 将经纬度坐标转换为屏幕内像素坐标
     * @param {array} coordinates - 经纬度坐标
     * @returns {array} - 屏幕内像素坐标
     */
    _lngLatToContainerPoint = coordinates => {
        const x = coordinates[0];
        const y = coordinates[1];
        const point = this._map.latLngToContainerPoint([y, x]);

        return [point.x, point.y];
    }

    /**
     * 生成瓦片像素坐标转换为经纬度坐标的转换函数
     * 在坐标转换类中使用，为了保证this对象正确，因此使用箭头函数来定义
     * @param {object}} tile 瓦片对象
     * @returns {function} 转换函数
     */
    getTilePointToLngLat(tile) {
        return coordinates => {
            const x = coordinates[0];
            const y = coordinates[1];
            const px = tile.size * tile.x + x;
            const py = tile.size * tile.y + y;

            return this._pixelToLngLat([px, py]);
        };
    }

    /**
     * 生成像素坐标转换为经纬度坐标的转换函数
     */
    getPixelToLngLat() {
        return coordinates => this._pixelToLngLat(coordinates);
    }
}
